import {
  type FC,
  type FunctionComponent,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Box } from "@mui/material";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useSignOut, useUser } from "@/partner/entities/auth";
import { useCurrentPlatformQuery } from "@/partner/entities/platforms";
import { useLiveEnabled } from "@/partner/entities/instance";
import { useHasBillingContracts } from "@/partner/entities/billing";
import { useSupportRequestsOpenCount } from "@/partner/entities/assistant";
import { AccessDeniedScreen } from "@/partner/features/auth";
import {
  ASSISTANT_FAB_CLEARANCE,
  PartnerAssistant,
  useAssistantDockInset,
} from "@/partner/features/partnerAssistant";
import { useAbility } from "@/partner/shared/lib";
import { MenuItems, PagePaths } from "@/partner/shared/constants";
import type { HeaderProps } from "@/partner/widgets/header";
import type { SidebarProps } from "@/partner/widgets/sidebar";
import {
  AccountMenu,
  Logo,
  MultiLevelMenu,
  PlatformCard,
  UserCard,
} from "@/partner/shared/ui";
import {
  filterMenuItems,
  getFirstAccessiblePath,
  hasMenuPath,
} from "@/partner/widgets/layout/lib/menuAccess";

export interface MainLayoutProps {
  header: FunctionComponent<HeaderProps>;
  sidebar: FunctionComponent<SidebarProps>;
}

export const Layout: FC<MainLayoutProps> = ({
  header: Header,
  sidebar: Sidebar,
}) => {
  const { user } = useUser();
  const ability = useAbility();
  const location = useLocation();
  const { data: platformData } = useCurrentPlatformQuery();
  const liveEnabled = useLiveEnabled();
  // Пункт «Биллинг» скрыт у организации без контрактов (спека §10): загрузка
  // не должна на мгновение прятать пункт у тех, кому он реально доступен
  // (см. комментарий в самом хуке), поэтому в фильтр меню значение уходит
  // сразу, не дожидаясь отдельного состояния загрузки здесь.
  const { hasContracts: hasBillingContracts } = useHasBillingContracts();
  const [signOut, { loading }] = useSignOut();
  const [isShowSideBar, setIsShowSideBar] = useState(false);
  // Помощник живёт в круглой кнопке в правом нижнем углу, шапка без него.
  const assistantEnabled = Boolean(user?.currentOrganization?.assistantEnabled);
  // Открытая боковая панель помощника стоит справа рядом со страницей:
  // область страницы сужается на её ширину.
  const assistantDock = useAssistantDockInset(assistantEnabled);

  // Пункты меню это обычные ссылки роутера, сами они ящик не закрывают. На
  // узком экране ящик временный и висел бы поверх уже открытой страницы,
  // поэтому закрываем его на смену адреса. На широком экране ящик
  // постоянный, там это состояние ни на что не влияет.
  useEffect(() => {
    setIsShowSideBar(false);
  }, [location.pathname]);

  const currentPlatform = platformData?.currentPlatform ?? null;

  const platformFeatures = currentPlatform?.features;
  const filteredMenuItems = useMemo(
    () =>
      filterMenuItems(
        MenuItems,
        ability,
        user,
        liveEnabled,
        hasBillingContracts,
        platformFeatures ?? []
      ),
    [ability, user, liveEnabled, hasBillingContracts, platformFeatures]
  );

  // Счётчик открытых обращений у пункта «Обращения» и у раздела «Аудитория».
  // Запрос уходит только тем, кому пункт виден (тот же гейт read
  // SupportRequest, что у пункта меню).
  const canSeeRequests = hasMenuPath(
    filteredMenuItems,
    PagePaths.audienceRequests
  );
  const openRequests = useSupportRequestsOpenCount({ skip: !canSeeRequests });
  const menuBadges = useMemo(
    () => ({ [PagePaths.audienceRequests]: openRequests.count }),
    [openRequests.count]
  );

  // Первая доступная страница после входа (спека, «Меню и маршруты»):
  // единственный маршрут с особым поведением — дашборд не показывает
  // "Недостаточно прав" на месте (как остальные страницы через
  // RequireAbility в router.tsx), а уводит на первый доступный пункт меню;
  // если доступных пунктов нет вовсе — AccessDeniedScreen на весь экран,
  // без Header/Sidebar.
  if (location.pathname === PagePaths.partner) {
    const dashboardAccessible = filteredMenuItems.some(
      (item) => !item.section && item.to === PagePaths.partner
    );
    if (!dashboardAccessible) {
      const firstAvailablePath = getFirstAccessiblePath(filteredMenuItems);
      if (firstAvailablePath) {
        return <Navigate to={firstAvailablePath} replace />;
      }
      return <AccessDeniedScreen variant="fullScreen" />;
    }
  }

  return (
    <>
      <Header
        accountMenu={
          <AccountMenu user={user} onSignOut={signOut} loading={loading} />
        }
        onMenuToggle={() => setIsShowSideBar((prev) => !prev)}
      />

      {assistantEnabled && <PartnerAssistant />}

      <Sidebar
        isOpen={isShowSideBar}
        logo={
          currentPlatform ? (
            <PlatformCard platform={currentPlatform} />
          ) : (
            <Logo />
          )
        }
        userCard={<UserCard user={user} />}
        menu={<MultiLevelMenu items={filteredMenuItems} badges={menuBadges} />}
        onClose={() => setIsShowSideBar(false)}
        onSignOut={signOut}
        signOutLoading={loading}
      />

      <Box
        component="main"
        sx={({ transitions }) => ({
          ml: { xs: 0, lg: "250px" },
          mr: `${assistantDock.inset}px`,
          p: 1,
          // Круглая кнопка помощника стоит в правом нижнем углу: последняя
          // строка страницы не прячется под ней. Пока открыта боковая
          // панель, кнопки нет.
          ...(assistantEnabled &&
            !assistantDock.inset && { pb: `${ASSISTANT_FAB_CLEARANCE}px` }),
          transition: assistantDock.animate
            ? transitions.create("margin-right", {
                duration: transitions.duration.shorter,
              })
            : "none",
        })}
      >
        <Outlet />
      </Box>
    </>
  );
};
